import { useId } from "react";

import styled from "styled-components";
import { Form } from "react-bootstrap";
import { AgencyTags, IconButton } from "@bagel-web/components";

import { CommonKVNode, CommonPresentationItem } from "../../../types";
import {
  ClientWorkMark,
  EditableValueCell,
  EditInput,
  EditTextArea,
  HighlightedText,
  MomentLink,
  MomentLinkCell,
  RowActionButton,
  ValueCell,
} from "../SimpleComponentVisualization";
import { NodeEditing } from "./useNodeEditing";

export const KeyCell = styled.td<{ $depth: number }>`
  padding-left: ${({ $depth }) => `${$depth * 1.5}rem`} !important;
  color: var(--bs-code-color) !important;
  white-space: nowrap;
`;

// In edit mode the key cell bisects into two regions: the indent and
// chevron toggle the collapse, and the region from the key text to the
// cell's edge opens the node's editor.
const EditableKeyCell = styled(KeyCell)`
  cursor: pointer;
`;

const EditableKeyRegion = styled.div`
  flex-grow: 1;
`;

// The kv row's key and value share one editor, so the actions reveal on the
// whole row rather than the value cell alone.
const NodeActionButton = styled(RowActionButton)`
  tr:hover &,
  &:focus-visible {
    opacity: 1;
  }
`;

// The editor open on this row: its state plus the shared refs and handlers.
// The key half and the value half apply together.
export type OpenNodeEditor = {
  state: NodeEditing;
  keyInputRef: React.RefObject<HTMLInputElement | null>;
  valueEditorRef: React.RefObject<HTMLDivElement>;
  onChange: (
    patch: Partial<Pick<NodeEditing, "segment" | "text" | "isList">>
  ) => void;
  onApply: () => void;
};

// The collapse toggle. Hidden on a leaf, but still taking its width so the
// key text lines up down the column.
export const CollapseChevron = ({
  visible,
  className,
  disabled,
}: {
  visible: boolean;
  className: string;
  disabled?: boolean;
}) => (
  <span
    className="me-1"
    style={visible ? { visibility: "visible" } : { visibility: "hidden" }}
  >
    <IconButton className={className} disabled={disabled} />
  </span>
);

// The row's key column: the chevron, the node's own key segment (with the
// delimiter after one that has children), and its agency tags.
export const KVKeyCell = ({
  depth,
  segment,
  suffix,
  tags,
  textFilter,
  chevron,
  isEditMode,
  editor,
  onToggleCollapse,
  onStartEdit,
}: {
  depth: number;
  segment: string;
  suffix: string;
  tags: CommonKVNode["agency_tags"];
  textFilter?: string;
  chevron: React.ReactNode;
  isEditMode?: boolean;
  editor: OpenNodeEditor | null;
  onToggleCollapse: () => void;
  onStartEdit: () => void;
}) => {
  const tagsView = <AgencyTags className="mt-2" tags={tags} />;

  if (editor) {
    return (
      <KeyCell $depth={depth}>
        <div className="d-flex">
          {chevron}
          <EditInput
            autoFocus={editor.state.focus === "key"}
            ref={editor.keyInputRef}
            value={editor.state.segment}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              editor.onChange({ segment: event.target.value })
            }
            // Clicks inside the editor must not toggle the row collapse.
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            // Tab moves between the pair without leaving the editor; only
            // tabbing backwards out of the key applies.
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Tab" && event.shiftKey) {
                editor.onApply();
              }
            }}
            data-testid="kv-store-key-input"
          />
          {suffix}
          {tagsView}
        </div>
      </KeyCell>
    );
  }

  if (isEditMode) {
    return (
      <EditableKeyCell
        $depth={depth}
        onClick={(event) => {
          event.stopPropagation();
          onToggleCollapse();
        }}
      >
        <div className="d-flex">
          {chevron}
          <EditableKeyRegion
            onClick={(event) => {
              event.stopPropagation();
              onStartEdit();
            }}
            data-testid="kv-store-editable-key"
          >
            <HighlightedText text={segment} textFilter={textFilter} />
            {suffix}
            {tagsView}
          </EditableKeyRegion>
        </div>
      </EditableKeyCell>
    );
  }

  return (
    <KeyCell $depth={depth}>
      {chevron}
      <HighlightedText text={segment} textFilter={textFilter} />
      {suffix}
      {tagsView}
    </KeyCell>
  );
};

// The row's value column. In edit mode the whole cell opens the editor and
// carries the row's actions; a structural node shows no value but still
// offers them. The open editor pairs the text with a List switch: on, each
// line of the text is one item; off, the text is the string itself.
export const KVValueCell = ({
  text,
  textFilter,
  compact,
  isEditMode,
  editor,
  onStartEdit,
  onAddChild,
  onDelete,
}: {
  // The node's value as shown; null on a structural node.
  text: string | null;
  textFilter?: string;
  compact: boolean;
  isEditMode?: boolean;
  editor: OpenNodeEditor | null;
  onStartEdit: () => void;
  onAddChild: () => void;
  onDelete: () => void;
}) => {
  const listSwitchId = useId();
  const valueView =
    text !== null ? (
      <HighlightedText text={text} textFilter={textFilter} />
    ) : null;

  if (editor) {
    return (
      <ValueCell $compactView={compact}>
        <div
          className="d-flex align-items-start gap-2"
          ref={editor.valueEditorRef}
          // Clicks inside the editor must not toggle the row collapse.
          onClick={(event) => event.stopPropagation()}
        >
          {/* Ahead of the textarea, so Tab from the key lands here before
              the value, and Tab out of the value still applies. */}
          <Form.Check
            type="switch"
            id={listSwitchId}
            label="List"
            className="text-nowrap mb-0"
            checked={editor.state.isList}
            onChange={() => editor.onChange({ isList: !editor.state.isList })}
            data-testid="kv-store-list-switch"
          />
          <EditTextArea
            className="flex-grow-1"
            autoFocus={editor.state.focus === "value"}
            value={editor.state.text}
            onChange={(event) => editor.onChange({ text: event.target.value })}
            // Tab moves between the fields without leaving the editor; only
            // tabbing forwards out of the value applies the change.
            onKeyDown={(event) => {
              if (event.key === "Tab" && !event.shiftKey) {
                editor.onApply();
              }
            }}
            data-testid="kv-store-edit-input"
          />
        </div>
      </ValueCell>
    );
  }

  if (isEditMode) {
    return (
      <EditableValueCell
        $compactView={compact}
        onClick={(event) => {
          // Editing, not collapsing: the row click stays out of it.
          event.stopPropagation();
          onStartEdit();
        }}
        data-testid="kv-store-editable-cell"
      >
        <div className="d-flex align-items-start gap-2">
          <div className="flex-grow-1">{valueView}</div>
          <NodeActionButton
            className="bi-pencil-fill"
            data-testid="kv-store-edit-node"
          />
          <NodeActionButton
            className="bi-node-plus-fill"
            data-testid="kv-store-add-child"
            onClick={(event) => {
              event.stopPropagation();
              onAddChild();
            }}
          />
          <NodeActionButton
            className="bi-trash-fill"
            data-testid="kv-store-delete-node"
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
          />
        </div>
      </EditableValueCell>
    );
  }

  return <ValueCell $compactView={compact}>{valueView}</ValueCell>;
};

export const KVMomentCell = ({
  item,
  hasValue,
  isEditMode,
  sessionId,
  agentId,
}: {
  item?: CommonPresentationItem;
  hasValue: boolean;
  isEditMode?: boolean;
  sessionId: string;
  agentId: string;
}) => (
  <MomentLinkCell>
    {isEditMode && hasValue && !item ? (
      <ClientWorkMark data-testid="kv-store-edited-marker" />
    ) : (
      item && <MomentLink sessionId={sessionId} agentId={agentId} item={item} />
    )}
  </MomentLinkCell>
);
